import React, { useEffect, useMemo, useState } from 'react';
import defaultTo from 'lodash/defaultTo';
import { Maybe, PaymentMethod, PaymentMethodType } from '@atlassian/cssxp-graphtypes';
import { InfoPanel } from '@css-xp/base';
import { Divider } from '@css-xp/base/divider';
import { PaymentMethodsTableV2 } from '@css-xp/base/payment-methods-table-v2';
import { Ach, PaymentMethodTypeV2, PaymentOnTerms } from '@css-xp/graphtypes/src/generated-types-new';
import { isAchPaymentMethod } from '@css-xp/ppc-utils';

interface PaymentMethodStepProps {
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  onFieldValueChange: (name: string, value: any) => void;
  selectedPaymentId?: string;
  termPaymentStatus?: Maybe<string> | null;
  isPaypalSupportEnabled?: boolean;
  isTermPaymentSelectionDisabled?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  headingMessage?: string;
  hideWarningMessage?: boolean;
  setPaymentMethodId?: any;
  version?: number;
  showHeader?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

export const PaymentMethodStep = ({
  availablePaymentMethods,
  onFieldValueChange,
  selectedPaymentId,
  termPaymentStatus,
  isPaypalSupportEnabled,
  isTermPaymentSelectionDisabled,
  isMultiCurrencyChangesEnabled,
  headingMessage,
  hideWarningMessage,
  setPaymentMethodId,
  version,
  showHeader,
  shouldShowACHPaymentMethod,
}: PaymentMethodStepProps) => {
  const deferredPaymentMethods = useMemo(() => {
    return availablePaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.DEFERRED) ?? [];
  }, [availablePaymentMethods]);

  const cardPaymentMethods = useMemo(() => {
    return availablePaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD) ?? [];
  }, [availablePaymentMethods]);

  const payPalPaymentMethods = useMemo(() => {
    return (
      availablePaymentMethods?.filter((paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.PAYPAL) ?? []
    );
  }, [availablePaymentMethods]);

  const achPaymentMethods = useMemo(() => {
    if (!shouldShowACHPaymentMethod) {
      return [];
    }
    return (availablePaymentMethods?.filter((paymentMethod: any) => isAchPaymentMethod(paymentMethod?.type)) ??
      []) as Ach[];
  }, [availablePaymentMethods, shouldShowACHPaymentMethod]);

  const defaultPaymentMethodId = useMemo(() => {
    const defaultPaymentMethod = availablePaymentMethods?.find((paymentMethod) => paymentMethod?.default);
    return isMultiCurrencyChangesEnabled ? '' : defaultTo(defaultPaymentMethod?.id, '');
  }, [availablePaymentMethods]);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(defaultPaymentMethodId);

  useEffect(() => {
    onFieldValueChange('defaultPaymentMethod', selectedPaymentMethodId);
    if (setPaymentMethodId) {
      setPaymentMethodId(selectedPaymentMethodId);
    }
  }, [onFieldValueChange, selectedPaymentMethodId, setPaymentMethodId]);

  useEffect(() => {
    if (selectedPaymentId) {
      setSelectedPaymentMethodId(selectedPaymentId);
    }
  }, [selectedPaymentId]);

  const handleSelectPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
  };

  const isTermPaymentAvailable = isTermPaymentSelectionDisabled ? false : deferredPaymentMethods.length > 0;

  const isDefaultMethodIsCard = cardPaymentMethods?.some(
    (paymentMethod) => paymentMethod?.id === defaultPaymentMethodId
  );

  const paymentMethodsTableProps = {
    cardPaymentMethods: cardPaymentMethods as PaymentMethod[],
    defaultPaymentMethodId,
    selectPaymentMethod: handleSelectPaymentMethod,
    selectedPaymentMethodId,
    header: headingMessage ? headingMessage : 'Select from existing methods',
    termPaymentMethods: deferredPaymentMethods as PaymentOnTerms[],
    isCreditManagementEnabled: true,
    isTermPaymentAvailable,
    isDefaultMethodIsCard,
    termPaymentStatus,
    payPalPaymentMethods,
    isPaypalSupportEnabled,
    version,
    showHeader,
    shouldShowACHPaymentMethod,
    achPaymentMethods: achPaymentMethods as Ach[],
  };

  return (
    <div>
      {!hideWarningMessage && (
        <>
          <div className="heading">
            <h3>Payment method</h3>
          </div>
          <Divider />
          <div className="term-payment-info-container">
            <InfoPanel
              type="warning"
              title="Select a payment method"
              message="You won't be able to switch to a payment term later if you select any other method right now"
            />
          </div>
        </>
      )}
      <PaymentMethodsTableV2 {...paymentMethodsTableProps} />
    </div>
  );
};
